import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { tdarrClient, tdarrCrud, tdarrGet, tdarrPost } from './api';
import { TdarrAuthInfo, TdarrBackup, TdarrBackupStatus, TdarrDocument, TdarrDupeStatus, TdarrDuplicatePair, TdarrJobReport, TdarrLibrary, TdarrMediaFile, TdarrNode, TdarrPieStats, TdarrResourceStats, TdarrServerClock, TdarrStagedFile, TdarrStatistics, TdarrStatus, TdarrTableResult, TdarrUpdateStatus } from './types';

export function useTdarrAuth() {
  return useQuery({
    queryKey: ['tdarr', 'auth'],
    queryFn: () => tdarrGet<TdarrAuthInfo>('/auth/verify-token'),
    retry: false,
    staleTime: 60000
  });
}

export function useTdarrStatus() {
  return useQuery({ queryKey: ['tdarr', 'status'], queryFn: () => tdarrGet<TdarrStatus>('/status'), refetchInterval: 10000 });
}

export function useTdarrAuthorised() {
  return useQuery({ queryKey: ['tdarr', 'authorised'], queryFn: () => tdarrPost<boolean>('/auth-status', { data: { saU: true } }), staleTime: 5 * 60 * 1000 });
}

export function useTdarrNodes() {
  return useQuery({ queryKey: ['tdarr', 'nodes'], queryFn: () => tdarrGet<Record<string, TdarrNode>>('/get-nodes'), refetchInterval: 5000 });
}

export function useTdarrResources() {
  return useQuery({ queryKey: ['tdarr', 'resources'], queryFn: () => tdarrPost<TdarrResourceStats>('/get-res-stats'), refetchInterval: 5000 });
}

export function useTdarrServerTime() {
  return useQuery<TdarrServerClock>({
    queryKey: ['tdarr', 'server-time'],
    queryFn: () => {
      const now = Date.now();
      return Promise.resolve({ value: now, syncedAt: now });
    },
    staleTime: Infinity
  });
}

export function useTdarrStatistics() {
  return useQuery({
    queryKey: ['tdarr', 'statistics'],
    queryFn: () => tdarrCrud<TdarrStatistics>({ collection: 'StatisticsJSONDB', mode: 'getById', docID: 'statistics' }),
    refetchInterval: 10000
  });
}

export function useTdarrLibraries() {
  return useQuery({
    queryKey: ['tdarr', 'collection', 'LibrarySettingsJSONDB'],
    queryFn: () => tdarrCrud<TdarrLibrary[]>({ collection: 'LibrarySettingsJSONDB', mode: 'getAll' })
  });
}

export function useTdarrStaged() {
  return useQuery({
    queryKey: ['tdarr', 'client', 'staged'],
    queryFn: () => tdarrClient<TdarrTableResult<TdarrStagedFile>>('staged', { opts: { table: 'table0' } }),
    refetchInterval: 10000
  });
}

export function useTdarrStatusTables() {
  return useQueries({
    queries: Array.from({ length: 7 }, (_, index) => ({
      queryKey: ['tdarr', 'client', 'status-tables', `table${index}`],
      queryFn: () => tdarrClient<TdarrTableResult<TdarrMediaFile>>('status-tables', { pageSize: 10, opts: { table: `table${index}` } }),
      refetchInterval: 10000
    }))
  });
}

export function useTdarrActivityTable<T>(clientType: 'status-tables' | 'staged' | 'jobs' | 'cache', options: {
  page: number; pageSize: number; filterKey?: string; filterValue?: string; sortKey?: string; sortDescending?: boolean; table?: string;
}) {
  const { page, pageSize, filterKey, filterValue, sortKey, sortDescending = false, table } = options;
  return useQuery({
    queryKey: ['tdarr', 'client', clientType, table, page, pageSize, filterKey, filterValue, sortKey, sortDescending],
    queryFn: () => tdarrClient<TdarrTableResult<T>>(clientType, {
      start: (page - 1) * pageSize,
      pageSize,
      filters: filterKey && filterValue ? [{ id: filterKey, value: filterValue }] : [],
      sorts: sortKey ? [{ id: sortKey, desc: sortDescending }] : [],
      opts: table ? { table } : {}
    }),
    placeholderData: keepPreviousData,
    refetchInterval: 10000
  });
}

export function useTdarrPieStats(libraryId = '') {
  return useQuery({ queryKey: ['tdarr', 'stats', 'pies', libraryId], queryFn: () => tdarrPost<TdarrPieStats>('/stats/get-pies', { data: { libraryId } }) });
}

export function useTdarrServerLog() {
  return useQuery({ queryKey: ['tdarr', 'logs', 'server'], queryFn: () => tdarrGet<string>('/get-server-log'), refetchInterval: 10000 });
}

export function useTdarrUpdateStatus() {
  return useQuery({
    queryKey: ['tdarr', 'update-status'],
    queryFn: () => tdarrPost<TdarrUpdateStatus>('/updater/check', { downloadUpdate: false, applyUpdate: false, resetUpdater: false }),
    staleTime: 60 * 60 * 1000
  });
}

export function useTdarrCollection(collection: string) {
  return useQuery({
    queryKey: ['tdarr', 'collection', collection],
    queryFn: () => tdarrCrud<TdarrDocument[]>({ collection, mode: 'getAll' })
  });
}

export function useTdarrBackups() {
  return useQuery({ queryKey: ['tdarr', 'backups'], queryFn: () => tdarrPost<TdarrBackup[]>('/get-backups') });
}

export function useTdarrBackupStatus() {
  return useQuery({ queryKey: ['tdarr', 'backup-status'], queryFn: () => tdarrPost<TdarrBackupStatus[]>('/get-backup-status'), refetchInterval: 1000 });
}

export function useTdarrDupeStatus() {
  return useQuery<TdarrDupeStatus>({ queryKey: ['tdarr', 'dupe-status'], queryFn: async () => ({}), enabled: false });
}

export function useTdarrDuplicateResults(page = 1, pageSize = 25) {
  return useQuery({
    queryKey: ['tdarr', 'client', 'dedupe', page, pageSize],
    queryFn: () => tdarrClient<TdarrTableResult<TdarrDuplicatePair>>('dedupe', { start: (page - 1) * pageSize, pageSize }),
    placeholderData: keepPreviousData,
    refetchInterval: 10000
  });
}

export function useTdarrSizeFiles() {
  return useQuery({
    queryKey: ['tdarr', 'client', 'size-explorer'],
    queryFn: () => tdarrClient<TdarrTableResult<TdarrMediaFile>>('search', { pageSize: 10000 }),
    staleTime: 30000
  });
}

export function useTdarrSearch(searchTerm: string) {
  const query = searchTerm.trim();
  return useQuery({
    queryKey: ['tdarr', 'search', query],
    queryFn: () => tdarrPost<TdarrMediaFile[]>('/search-db', { data: { string: query, greaterThanGB: 0, lessThanGB: 1000000 } }),
    enabled: Boolean(query),
    staleTime: 30000
  });
}

export function useTdarrJobReport(jobFileId: string) {
  const parts = jobFileId.replace(/\.txt$/, '').split('()');
  const valid = parts.length === 5 && Boolean(parts[0] && parts[3]);
  return useQuery({
    queryKey: ['tdarr', 'job-report', jobFileId],
    queryFn: () => tdarrPost<TdarrJobReport>('/read-job-file', { data: { footprintId: parts[0], jobId: parts[3], jobFileId } }),
    enabled: valid,
    refetchInterval: (query) => query.state.data?.isJobRunning ? 10000 : false
  });
}
